import React, { useEffect, useState } from "react";
import CrudForm from "../../components/dashboard/form/CrudForm";
import DashboardWrapper from "../../components/DashboardWrapper";
import { useSingletonCrud } from "../../hooks/useSingletonCrud";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const LOCAL_STORAGE_KEY = "paypalSettingData";

export default function PaypalPayment() {
  const navigate = useNavigate();

  const { loading, errors, fetchSingleton, updateSingleton } =
    useSingletonCrud("backend/paypal-setting");

  const [initialData, setInitialData] = useState(null);
  const [showSecret, setShowSecret] = useState(false); // 👁 toggle

  const allowedFields = [
    "paypal_client_id",
    "paypal_secret_key",
    "status",
  ];

  const isEqual = (obj1, obj2) => {
    if (!obj1 || !obj2) return false;
    return allowedFields.every((key) => obj1[key] === obj2[key]);
  };

  useEffect(() => {
    const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (cached) {
      try {
        setInitialData(JSON.parse(cached));
      } catch (e) {
        console.warn("Failed to parse cached PayPal data:", e);
      }
    }

    const fetchData = async () => {
      try {
        const paypal = await fetchSingleton();

        const formatted = {
          paypal_client_id: paypal?.paypal_client_id ?? "",
          paypal_secret_key: paypal?.paypal_secret_key ?? "",
          status: paypal?.status ? 1 : 0,
        };

        if (!isEqual(formatted, initialData)) {
          setInitialData(formatted);
          localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify(formatted)
          );
        }
      } catch (error) {
        console.error("Error fetching PayPal data:", error);
        Swal.fire("Error", "Failed to load PayPal settings", "error");
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (formData) => {
    try {
      const dataToSend = {
        paypal_client_id: formData.paypal_client_id,
        paypal_secret_key: formData.paypal_secret_key,
        status: Number(formData.status) === 1,
      };

      const res = await updateSingleton(dataToSend);

      Swal.fire(
        "Success",
        "PayPal settings updated successfully",
        "success"
      );

      const updatedData = {
        paypal_client_id: res.paypal_client_id ?? "",
        paypal_secret_key: res.paypal_secret_key ?? "",
        status: res.status ? 1 : 0,
      };

      setInitialData(updatedData);
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify(updatedData)
      );
    } catch (error) {
      console.error("Error updating PayPal settings:", error);

      let msg = "Failed to update PayPal settings";
      if (error.response?.data) {
        if (error.response.data.errors) {
          msg = Object.values(error.response.data.errors)
            .flat()
            .join(", ");
        } else if (error.response.data.message) {
          msg = error.response.data.message;
        }
      }

      Swal.fire("Error", msg, "error");
    }
  };

  const shouldShowLoading = loading && !initialData;

  return (
    <DashboardWrapper>
      <div className="container-xxl flex-grow-1 p-4">
        <h1 className="h4">PayPal Configuration</h1>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            className="btn btn-secondary d-flex align-items-center"
            onClick={() => navigate(-1)}
          >
            <i className="bx bx-arrow-back" style={{ marginTop: "2px" }}></i>
            <span className="ms-1">Back</span>
          </button>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                Manage PayPal Settings
              </div>

              <div className="card-body">
                {shouldShowLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "200px" }}
                  >
                    <div className="spinner-border text-primary" />
                  </div>
                ) : (
                  <>
                    <CrudForm
                      initialData={initialData || {}}
                      onSubmit={handleSubmit}
                      allowedFields={allowedFields}
                      customFields={{
                        paypal_client_id: {
                          label: "PayPal Client ID",
                          type: "text",
                          placeholder: "Enter PayPal Client ID",
                        },

                        paypal_secret_key: {
                          label: "PayPal Secret Key",
                          type: showSecret ? "text" : "password",
                          placeholder: "Enter PayPal Secret Key",
                          inputGroup: {
                            right: (
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() =>
                                  setShowSecret((prev) => !prev)
                                }
                              >
                                <i
                                  className={`bx ${
                                    showSecret ? "bx-hide" : "bx-show"
                                  }`}
                                ></i>
                              </button>
                            ),
                          },
                        },

                        status: {
                          label: "Status",
                          type: "select",
                          options: [
                            { value: 1, label: "Active" },
                            { value: 0, label: "Inactive" },
                          ],
                        },
                      }}
                    />

                    {errors.fetch && (
                      <div className="text-danger mt-2">
                        Error: {errors.fetch}
                      </div>
                    )}

                    {errors.update && (
                      <div className="text-danger mt-2">
                        Error: {errors.update}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
